import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import {
  type FaqCheck,
  nationalDigits,
} from '../../../features/onboarding/onboardingChecklist';
import {
  useConfirmChecklistItem,
  useDocumentActions,
  useHoursLookup,
  useOnboardingDocuments,
  useStripeConnect,
} from '../../../features/onboarding/useOnboardingChecklist';
import { useScopedQueryKey } from '../../../hooks/useScopedQueryKey';
import { formatPhone } from '../../../shared';
import {
  BlockSkeleton,
  Button,
  Callout,
  CheckProgress,
  Dialog,
  Embedded,
  Field,
  FileDrop,
  formatFileSize,
  IconCopy,
  IconDocument,
  IconExternal,
  Input,
  PhoneInput,
  Reason,
  SavedNote,
  Spinner,
  SUPPORT_EMAIL,
  useSavedAt,
} from '../../../ui/v2';
import {
  ACCEPTED_FILE_COPY,
  ACCEPTED_FILE_TYPES,
  validateUploadFiles,
} from '../../../utils/fileUploadValidation';
import {
  OpenTablePath,
  openGuestCenter,
  useOrdersIntegration,
} from '../../Integrations/integrationParts';
import { WeeklyHoursEditor } from '../../StoreInfo/v2/HoursSection';
import NotificationsSection from '../../StoreInfo/v2/NotificationsSection';
import {
  useStoreActions,
  useStoreHoursQuery,
  useStoreInfoQuery,
} from '../../StoreInfo/v2/data';
import { type StepProps, StepFooter, StepIntro, StepPart } from './StepFlow';

/**
 * The Set up steps, each done where it's listed. Most reuse the Store info
 * section that owns the setting, embedded so it drops its page furniture;
 * the checklist refreshes when they save (see `useChecklistFollowsSaves`).
 */

// --- Opening hours -----------------------------------------------------------

// With no hours set, we look the store up on Google first and copy what its
// listing says; the store checks them and confirms (or saves its changes).
export const HoursStep: React.FC<StepProps> = ({ item, checklist }) => {
  const hours = useStoreHoursQuery();
  const lookup = useHoursLookup();
  const confirm = useConfirmChecklistItem();
  const tried = useRef(false);
  // Confirming doesn't save the table, so edits have to be saved first.
  const [unsaved, setUnsaved] = useState(false);
  const data = hours.data;
  const empty = Boolean(
    data &&
      !data.hours.some(
        (row) => !row.is_closed && row.opening_time && row.closing_time,
      ),
  );
  const { mutate: look } = lookup;
  useEffect(() => {
    if (tried.current || !empty || !checklist.can_confirm) return;
    if (checklist.status === 'live') return;
    tried.current = true;
    look();
  }, [empty, checklist.can_confirm, checklist.status, look]);

  const google = item.status !== 'done' ? data?.from_google : null;
  return (
    <>
      {lookup.isPending && (
        <p
          role="status"
          className="mb-4 inline-flex items-center gap-2 text-v2-13 text-v2-fg-3"
        >
          <Spinner /> Looking for your hours on Google…
        </p>
      )}
      {google && (
        // A tinted note, not a box: it sits inside the open step.
        <Callout tone="accent" well className="mb-5">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <p className="min-w-0 flex-[1_1_280px] text-v2-13 leading-5 text-v2-fg-2">
              From your Google listing
              {google.name ? (
                <>
                  {' '}
                  for{' '}
                  <span className="font-medium text-v2-fg">{google.name}</span>
                </>
              ) : null}
              . Fix anything that’s off, or confirm they’re right.
            </p>
            {checklist.can_confirm ? (
              <Button
                variant="primary"
                loading={confirm.isPending}
                disabledReason={unsaved ? 'Save your changes first' : undefined}
                onClick={() => confirm.mutate(item.key)}
              >
                These hours are right
              </Button>
            ) : (
              <Reason>An owner or a manager can confirm them.</Reason>
            )}
          </div>
          {unsaved && checklist.can_confirm && (
            <Reason className="mt-2">
              Save your changes below. Saving confirms them too.
            </Reason>
          )}
          {confirm.error && (
            <p className="mt-2 text-v2-12 text-v2-danger">
              {confirm.error.message}
            </p>
          )}
        </Callout>
      )}
      <Embedded>
        <WeeklyHoursEditor manualOnly onDirtyChange={setUnsaved} />
      </Embedded>
    </>
  );
};

// --- Notification contacts ---------------------------------------------------

// The section's own description says what it's for.
export const ContactsStep: React.FC<StepProps> = () => (
  <Embedded>
    <NotificationsSection />
  </Embedded>
);

// --- Upload your documents --------------------------------------------------

/** A row in a list: a document on the server, one uploading here, or the FAQ link. */
interface DocumentRow {
  id: string;
  name: string;
  size: number;
  faq: boolean;
  status: 'uploaded' | 'uploading' | 'removing' | 'failed';
  error?: string;
  /** The FAQ page: the row links to it and has no size. */
  href?: string;
}

const FAQ_LINK_ROW = 'faq-url';

/**
 * The documents the agent learns from. The FAQs are required, uploaded or as
 * a link to the FAQ page; menus, policies and the rest are optional. Files
 * from the onboarding form are already here.
 */
export const DocumentsStep: React.FC<StepProps> = () => {
  const page = useOnboardingDocuments();
  const { upload, remove, setFaqUrl } = useDocumentActions();
  const [pending, setPending] = useState<DocumentRow[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A rejection only speaks to the upload made here; one kept from an
  // earlier session says nothing the still-open step doesn't.
  const [faqTried, setFaqTried] = useState(false);
  const nextId = useRef(0);

  const add = async (files: File[], faq: boolean) => {
    setError(null);
    if (faq) setFaqTried(true);
    // Every file shows at once; they then go up one at a time, since a
    // mutation only reports back to its latest call.
    const queued = await Promise.all(
      files.map(async (file) => ({
        file,
        id: `pending-${nextId.current++}`,
        problem: await validateUploadFiles([file]),
      })),
    );
    setPending((rows) => [
      ...rows,
      ...queued.map(({ file, id, problem }) => ({
        id,
        name: file.name,
        size: file.size,
        faq,
        status: problem ? ('failed' as const) : ('uploading' as const),
        error: problem ? problem.replace(`${file.name}: `, '') : undefined,
      })),
    ]);
    for (const { file, id, problem } of queued) {
      if (problem) continue;
      try {
        await upload.mutateAsync({ file, faq });
        // Once uploaded, the file is in the list from the server.
        setPending((rows) => rows.filter((r) => r.id !== id));
      } catch (e) {
        setPending((rows) =>
          rows.map((r) =>
            r.id === id
              ? { ...r, status: 'failed', error: (e as Error).message }
              : r,
          ),
        );
      }
    }
  };

  const drop = (row: DocumentRow) => {
    if (row.id.startsWith('pending-')) {
      setPending((rows) => rows.filter((r) => r.id !== row.id));
      return;
    }
    setError(null);
    setRemoving(row.id);
    const done = {
      onError: (e: Error) => setError(e.message),
      onSettled: () => setRemoving(null),
    };
    if (row.id === FAQ_LINK_ROW) setFaqUrl.mutate('', done);
    else remove.mutate(Number(row.id), done);
  };

  if (page.isPending) return <BlockSkeleton rows={3} />;
  if (page.isError) return <Reason>{page.error.message}</Reason>;
  const { documents, faq_url, faq_check, can_upload } = page.data;
  const checking = faq_check?.status === 'checking';
  const status = (id: string) =>
    removing === id ? ('removing' as const) : ('uploaded' as const);
  const rows: DocumentRow[] = [
    ...documents.map((doc) => ({
      id: String(doc.id),
      name: doc.name,
      size: doc.size_bytes,
      faq: doc.is_faq,
      status: status(String(doc.id)),
    })),
    ...(faq_url
      ? [
          {
            id: FAQ_LINK_ROW,
            name: faq_url,
            size: 0,
            faq: true,
            status: status(FAQ_LINK_ROW),
            href: faq_url,
          },
        ]
      : []),
    ...pending,
  ];
  const faqRows = rows.filter((row) => row.faq);
  const otherRows = rows.filter((row) => !row.faq);
  // Until the FAQs are in (or on their way), the step asks for them.
  const hasFaqs = checking || faqRows.some((row) => row.status !== 'failed');

  return (
    <>
      <StepPart title="FAQs" aside="Required">
        <FaqCheckDialog check={checking ? faq_check : null} />
        {faqTried && faq_check?.status === 'rejected' && !hasFaqs && (
          <Callout tone="danger" className="mb-3">
            Please upload a document containing commonly asked information.
            <span
              className="mt-0.5 block truncate text-v2-12 text-v2-fg-3"
              title={faq_check.name}
            >
              {faq_check.name}
            </span>
          </Callout>
        )}
        <DocumentList
          rows={faqRows}
          label="Your FAQs"
          canRemove={can_upload}
          onRemove={drop}
        />
        {!hasFaqs &&
          (can_upload ? (
            <>
              <FileDrop
                files={[]}
                onAdd={(list) => void add(list, true)}
                onRemove={() => undefined}
                accept={ACCEPTED_FILE_TYPES}
                hint={ACCEPTED_FILE_COPY}
                label="Upload your FAQs"
                multiple={false}
                className={faqRows.length ? 'mt-3' : undefined}
              />
              <FaqLinkForm
                saving={setFaqUrl.isPending}
                onSave={(url, done) =>
                  setFaqUrl.mutate(url, {
                    onSuccess: () => done(null),
                    onError: (e) => done(e.message),
                  })
                }
              />
            </>
          ) : (
            <Reason>An owner or a manager adds the FAQs.</Reason>
          ))}
      </StepPart>
      <StepPart title="Other documents" aside="Optional">
        <p className="mb-3 text-v2-13 text-v2-fg-2">
          Menus, policies and guides help your agent answer more.
        </p>
        <DocumentList
          rows={otherRows}
          label="Other documents"
          canRemove={can_upload}
          onRemove={drop}
          className="mb-3"
        />
        {can_upload && (
          // Optional, so it stays small until a file is dragged over the page.
          <FileDrop
            compact
            files={[]}
            onAdd={(list) => void add(list, false)}
            onRemove={() => undefined}
            accept={ACCEPTED_FILE_TYPES}
            hint={ACCEPTED_FILE_COPY}
            label="Upload other documents"
          />
        )}
      </StepPart>
      {error && (
        <Callout tone="danger" className="mt-3">
          {error}
        </Callout>
      )}
    </>
  );
};

const FAQ_CHECK_STEPS = [
  'Questions',
  'Policies',
  'Hours',
  'Menu',
  'Location',
] as const;

/**
 * While GPT Luna reads a file given as the FAQs. It takes a few seconds and
 * the step can't go on without it, so it holds the screen; nothing says it
 * can be closed.
 */
const FaqCheckDialog: React.FC<{ check: FaqCheck | null | undefined }> = ({
  check,
}) => (
  <Dialog
    open={!!check}
    onClose={() => undefined}
    dismissible={false}
    size="sm"
    title="Checking your FAQs"
  >
    <CheckProgress
      icon={<IconDocument size={20} />}
      message={
        <>
          Reading <span className="font-medium text-v2-fg">{check?.name}</span>
        </>
      }
      steps={FAQ_CHECK_STEPS}
    />
  </Dialog>
);

/** The other way to give the FAQs: a link to the store's FAQ page. */
const FaqLinkForm: React.FC<{
  saving: boolean;
  onSave: (url: string, done: (error: string | null) => void) => void;
}> = ({ saving, onSave }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="mt-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        onSave(url.trim(), setError);
      }}
    >
      <Field label="Or link to your FAQ page" error={error ?? undefined}>
        {(id, describedBy) => (
          <div className="flex flex-wrap gap-2">
            <Input
              id={id}
              aria-describedby={describedBy}
              type="url"
              inputMode="url"
              placeholder="yourrestaurant.com/faq"
              value={url}
              invalid={!!error}
              onChange={(e) => setUrl(e.target.value)}
              className="min-w-[220px] flex-1"
            />
            <Button
              type="submit"
              variant="secondary"
              loading={saving}
              disabledReason={url.trim() ? undefined : 'Paste the link first'}
            >
              Save link
            </Button>
          </div>
        )}
      </Field>
    </form>
  );
};

const DocumentList: React.FC<{
  rows: DocumentRow[];
  label: string;
  canRemove: boolean;
  onRemove: (row: DocumentRow) => void;
  className?: string;
}> = ({ rows, label, canRemove, onRemove, className }) =>
  rows.length ? (
    <ul aria-label={label} className={clsx('space-y-2', className)}>
      {rows.map((row) => (
        <DocumentItem
          key={row.id}
          row={row}
          canRemove={canRemove}
          onRemove={() => onRemove(row)}
        />
      ))}
    </ul>
  ) : null;

const DocumentItem: React.FC<{
  row: DocumentRow;
  canRemove: boolean;
  onRemove: () => void;
}> = ({ row, canRemove, onRemove }) => {
  const busy = row.status === 'uploading' || row.status === 'removing';
  return (
    <li className="rounded-v2-lg border border-v2-line bg-v2-surface px-3 py-2.5">
      <div className="flex items-center gap-3">
        <FileBadge name={row.name} link={!!row.href} />
        <div className="min-w-0 flex-1">
          {row.href ? (
            <a
              href={row.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate text-v2-13 font-medium text-v2-accent underline-offset-2 hover:underline"
              title={row.name}
            >
              {row.name}
            </a>
          ) : (
            <p
              className="truncate text-v2-13 font-medium text-v2-fg"
              title={row.name}
            >
              {row.name}
            </p>
          )}
          <p className="v2-num text-v2-12 text-v2-fg-3">
            {row.href ? 'Your FAQ page' : formatFileSize(row.size)}
            {row.status === 'uploading' && ' · Uploading…'}
          </p>
        </div>
        {busy ? (
          <Spinner />
        ) : (
          canRemove && (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Remove ${row.name}`}
              onClick={onRemove}
            >
              Remove
            </Button>
          )
        )}
      </div>
      {row.error && (
        <p className="mt-1.5 pl-[52px] text-v2-12 text-v2-danger">
          {row.error}
        </p>
      )}
    </li>
  );
};

/** "PDF", "DOC", "LINK"…: the row's kind at a glance. */
const FileBadge: React.FC<{ name: string; link?: boolean }> = ({
  name,
  link,
}) => {
  const ext = link ? '' : (name.split('.').pop()?.toLowerCase() ?? '');
  const label = link
    ? 'LINK'
    : ext === 'docx'
      ? 'DOC'
      : ext === 'jpeg'
        ? 'JPG'
        : ext.toUpperCase();
  return (
    <span
      aria-hidden
      className={clsx(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-v2 text-v2-11 font-semibold',
        ext === 'pdf'
          ? 'bg-v2-danger/10 text-v2-danger'
          : 'bg-v2-accent/10 text-v2-accent',
      )}
    >
      {label.slice(0, 4) || 'FILE'}
    </span>
  );
};

// --- Transfer line -----------------------------------------------------------

/**
 * The number the agent transfers callers to. It can't be the main number:
 * that one forwards to the agent once the store is live, so a transfer
 * would come straight back.
 */
export const TransferLineStep: React.FC<StepProps> = ({ item, checklist }) => {
  const info = useStoreInfoQuery();
  const { update } = useStoreActions();
  // Stored as +12015550188; PhoneInput shows it as +1 (201) 555-0188.
  const saved = info.data?.location_secondary_phone_number ?? '';
  const [value, setValue] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, markSaved] = useSavedAt();
  useEffect(() => setValue(saved), [saved]);

  if (info.status === 'pending') return <BlockSkeleton rows={2} />;

  const main =
    info.data?.location_primary_phone_number || checklist.main_number;
  const digits = nationalDigits(value);
  const changed = digits !== nationalDigits(saved);
  // North American numbers are always 10 digits; elsewhere, E.164 allows
  // 8 to 15 with the country code.
  const northAmerican =
    !value.trim().startsWith('+') || value.trim().startsWith('+1');
  const allDigits = value.replace(/\D/g, '');
  const complete = northAmerican
    ? digits.length === 10
    : allDigits.length >= 8 && allDigits.length <= 15;
  const isMain = Boolean(main) && digits === nationalDigits(main ?? '');
  // Set, and not the main number, yet still open: a transfer that's
  // switched on has nowhere to go. That's set up with the team.
  const otherIssue =
    item.status !== 'done' &&
    Boolean(nationalDigits(saved)) &&
    nationalDigits(saved) !== nationalDigits(main ?? '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await update({ location_secondary_phone_number: value.trim() });
      markSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That didn’t save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <StepIntro>
        When a caller asks for a person, your agent transfers them here. Use a
        number that rings at the restaurant, like a second line or a manager’s
        mobile, not your main number: that one will forward to your agent.
      </StepIntro>
      {otherIssue && !changed && (
        <Callout tone="warning" className="mb-4 max-w-[560px]">
          {item.detail} Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium underline">
            {SUPPORT_EMAIL}
          </a>{' '}
          and we’ll sort it out with you.
        </Callout>
      )}
      {checklist.can_confirm ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Field
            label="Transfer number"
            error={
              isMain ? 'That’s your main number. Use a second one.' : undefined
            }
            className="w-full max-w-[340px]"
          >
            {(id, describedBy) => (
              <PhoneInput
                id={id}
                aria-describedby={describedBy}
                value={value}
                invalid={isMain}
                disabled={saving}
                onChange={(next) => {
                  setValue(next);
                  setError(null);
                }}
              />
            )}
          </Field>
          <StepFooter>
            <Button
              type="submit"
              variant="primary"
              loading={saving}
              disabledReason={
                !changed
                  ? 'Change the number first'
                  : !complete
                    ? northAmerican
                      ? 'Enter a 10-digit number'
                      : 'Enter the whole number'
                    : isMain
                      ? 'Use a number other than your main one'
                      : undefined
              }
            >
              Save number
            </Button>
            <SavedNote when={savedAt} />
          </StepFooter>
        </form>
      ) : (
        <p className="text-v2-13 text-v2-fg">
          {saved ? formatPhone(saved) : 'No transfer number yet.'}{' '}
          <Reason>Your store’s owner or a manager changes this.</Reason>
        </p>
      )}
      {error && (
        <Callout tone="danger" className="mt-3 max-w-[560px]">
          {error}
        </Callout>
      )}
    </>
  );
};

// --- Connect OpenTable -------------------------------------------------------

/**
 * OpenTable is switched on inside OpenTable, so this step is the way there
 * and a way to check. Coming back to this tab refreshes it on its own.
 */
export const OpenTableStep: React.FC<StepProps> = ({ item }) => {
  const client = useQueryClient();
  const key = useScopedQueryKey('onboarding-checklist');
  const [checking, setChecking] = useState(false);
  if (item.status === 'done') {
    return (
      <Reason>OpenTable is connected. Your agent books tables there.</Reason>
    );
  }
  return (
    <>
      <StepIntro>
        Your agent books tables straight into OpenTable. You turn it on from
        your OpenTable account; it takes a minute.
      </StepIntro>
      <ol className="flex flex-col gap-3">
        <li className="flex gap-3">
          <StepNumber n={1} />
          <div>
            <p className="text-v2-13 text-v2-fg">
              Open OpenTable Guest Center.
            </p>
            <Button
              className="mt-2"
              variant="primary"
              onClick={openGuestCenter}
              trailing={<IconExternal size={12} />}
            >
              Open Guest Center
            </Button>
          </div>
        </li>
        <li className="flex gap-3">
          <StepNumber n={2} />
          <OpenTablePath />
        </li>
        <li className="flex gap-3">
          <StepNumber n={3} />
          <div>
            <p className="text-v2-13 text-v2-fg">
              Come back here. This step ticks itself once OpenTable connects.
            </p>
            <Button
              className="mt-2"
              size="sm"
              loading={checking}
              onClick={async () => {
                setChecking(true);
                try {
                  await client.invalidateQueries({ queryKey: key });
                } finally {
                  setChecking(false);
                }
              }}
            >
              Check again
            </Button>
          </div>
        </li>
      </ol>
    </>
  );
};

const StepNumber: React.FC<{ n: number }> = ({ n }) => (
  <span
    aria-hidden
    className="v2-num mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-v2-sunken text-v2-11 font-semibold text-v2-fg-2"
  >
    {n}
  </span>
);

// --- Ordering system ---------------------------------------------------------

/** Who on our team connects ordering systems; stores invite this email. */
export const ORDERING_CONTACT = { name: 'Ifi', email: 'ifi@askarthur.io' };

/**
 * The agent sends orders into the store's ordering system. Our team makes
 * that connection, so the store only invites our contact and says so here;
 * the step then waits on us.
 */
export const OrderingStep: React.FC<StepProps> = ({ item, checklist }) => {
  const confirm = useConfirmChecklistItem();
  const orders = useOrdersIntegration();

  if (orders.loading) return <BlockSkeleton rows={2} />;
  const system = orders.posInfo?.label ?? 'your ordering system';
  return (
    <>
      <StepIntro>
        Your agent sends phone orders into {system}. Our team connects it to
        your menu, so we need an invite. Add the email below as a user in{' '}
        {system}, then let us know here.
      </StepIntro>
      <OrderingContact />
      {confirm.error && (
        <Callout tone="danger" className="mb-3">
          {(confirm.error as Error).message}
        </Callout>
      )}
      <StepFooter>
        {checklist.can_confirm ? (
          <Button
            variant="primary"
            loading={confirm.isPending}
            onClick={() => confirm.mutate(item.key)}
          >
            I’ve invited {ORDERING_CONTACT.name}
          </Button>
        ) : (
          <Reason>Your store’s owner or a manager confirms this.</Reason>
        )}
      </StepFooter>
    </>
  );
};

const OrderingContact: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ORDERING_CONTACT.email);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-3 rounded-v2-lg border border-v2-line bg-v2-sunken/40 px-4 py-3">
      <span
        aria-hidden
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-v2-accent/10 text-v2-14 font-semibold text-v2-accent"
      >
        {ORDERING_CONTACT.name[0]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-v2-13 text-v2-fg-2">
          <span className="font-semibold text-v2-fg">
            {ORDERING_CONTACT.name}
          </span>{' '}
          will be your ordering integration contact.
        </p>
        <a
          href={`mailto:${ORDERING_CONTACT.email}`}
          className="text-v2-14 font-medium text-v2-fg underline-offset-2 hover:underline"
        >
          {ORDERING_CONTACT.email}
        </a>
      </div>
      <Button
        size="sm"
        variant="secondary"
        leading={<IconCopy size={13} />}
        onClick={() => void copy()}
      >
        {copied ? 'Copied' : 'Copy email'}
      </Button>
    </div>
  );
};

// --- Connect Stripe ----------------------------------------------------------

/**
 * Stripe's onboarding runs on Stripe's site; the store comes back to this
 * step (`?stripe=return`), which then checks the account.
 */
export const StripeStep: React.FC<
  StepProps & { stripe: ReturnType<typeof useStripeConnect> }
> = ({ item, checklist, stripe }) => {
  if (item.status === 'done') {
    return (
      <Reason>
        Stripe is connected. Callers who pay online pay you there.
      </Reason>
    );
  }
  return (
    <>
      <StepIntro>
        You’ll create or link a Stripe account on Stripe’s site, then come back
        here. Payments go straight to your Stripe account.
      </StepIntro>
      {stripe.start.error && (
        <Callout tone="danger" className="mb-3">
          {(stripe.start.error as Error).message}
        </Callout>
      )}
      <StepFooter>
        {checklist.can_connect_stripe ? (
          <Button
            variant="primary"
            loading={stripe.start.isPending || stripe.start.isSuccess}
            trailing={<IconExternal size={12} />}
            onClick={() =>
              stripe.start.mutate(undefined, {
                onSuccess: (url) => {
                  window.location.href = url;
                },
              })
            }
          >
            Connect with Stripe
          </Button>
        ) : (
          <Reason>Your store’s owner connects Stripe.</Reason>
        )}
      </StepFooter>
    </>
  );
};
